import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Settings, MessageCircle, BookOpen, Brain, Users } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { useFont } from '../contexts/FontContext';
import { useAuth } from '../contexts/AuthContext';
import BackButton from '../components/BackButton';
import AdminComments from '../components/admin/AdminComments';
import AdminCourses from '../components/admin/AdminCourses';
import AdminQuestions from '../components/admin/AdminQuestions';
import AdminUsers from '../components/admin/AdminUsers';

export default function AdminDashboard() {
  const { theme } = useTheme();
  const { font } = useFont();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState<'comments' | 'courses' | 'questions' | 'users'>('comments');

  // Redirect if not admin
  useEffect(() => {
    if (user && user.role !== 'admin') {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  if (user?.role !== 'admin') {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50" style={{ fontFamily: font.family }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-8">
          <BackButton />
        </div>

        <div className="flex items-center mb-8">
          <Settings className={`h-8 w-8 text-${theme.text} mr-3`} />
          <h1 className={`text-3xl font-bold text-${theme.text}`}>
            Administration
          </h1>
        </div>

        {/* Tabs */}
        <div className="mb-8">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              {[
                { id: 'comments', label: 'Commentaires', icon: MessageCircle },
                { id: 'courses', label: 'Cours', icon: BookOpen },
                { id: 'questions', label: 'Questions QCM', icon: Brain },
                { id: 'users', label: 'Utilisateurs', icon: Users }
              ].map(({ id, label, icon: Icon }) => (
                <button
                  key={id}
                  onClick={() => setActiveTab(id as any)}
                  className={`
                    flex items-center py-2 px-1 border-b-2 font-medium text-sm
                    ${activeTab === id
                      ? `border-${theme.primary} text-${theme.primary}`
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }
                  `}
                >
                  <Icon className="h-5 w-5 mr-2" />
                  {label}
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Content */}
        <div className="bg-white rounded-xl shadow-sm">
          <div className="p-6">
            {activeTab === 'comments' && <AdminComments />}
            {activeTab === 'courses' && <AdminCourses />}
            {activeTab === 'questions' && <AdminQuestions />}
            {activeTab === 'users' && <AdminUsers />}
          </div>
        </div>
      </div>
    </div>
  );
}